import React, { Fragment, useEffect, useState } from 'react';
import { DatePicker, Button, Input, Row, Col, Select, Typography, Space } from "antd";
import dayjs from "dayjs";

import {
    downloadProductChangesFromOtherExcel,
    fetchProductChangesFromOther,
    fetchProductChangesFromOtherFilters
} from "../../api/product-change";
import {
    ProductChangeFromOtherFilterType,
    productChangeFromOtherSelectedFiltersInitialState,
    ProductChangeFromOtherSelectedFiltersType,
    ProductChangeFromOtherSelectedParamsType
} from "../PrescriptionView/types";
import ProductChangeFromOtherTable from "./ProductChangeFromOtherTable";
import ProductChangeResponse from "../../types/ProductChangeResponse";
import ProductChangeFromOtherDTO from "../../types/ProductChangeFromOtherDTO";
import { downloadExcelData } from "../../data/util";
import { parseDate } from "../../util/date";
import { useTexts } from "../../hooks/useTexts";

const { Title, Text } = Typography;

const ProductChangeFromOtherTab = (): JSX.Element => {
    const { t } = useTexts();
    const [data, setData] = useState<ProductChangeResponse<ProductChangeFromOtherDTO>>({
        items: [],
        pagination: {
            page: 0,
            pageSize: 10,
            pageCount: 0,
            elementCount: 0,
        }
    });

    const [filters, setFilters] = useState<ProductChangeFromOtherFilterType>({
        productChanger: '', initialProducts: [], places: [], users: []
    });

    const [selectedFilters, setSelectedFilters] = useState<ProductChangeFromOtherSelectedFiltersType>(productChangeFromOtherSelectedFiltersInitialState);
    const [loading, setLoading] = useState(false);

    const getParams = (filters: ProductChangeFromOtherSelectedFiltersType): ProductChangeFromOtherSelectedParamsType =>
    ({
        productChanger: filters.productChanger,
        initialProductIds: filters.initialProductIds,
        placeIds: filters.placeIds,
        prescriptionUserIds: filters.prescriptionUserIds,
        page: filters.page,
        pageSize: filters.pageSize,
        sortDir: filters.sortDir,
        sortBy: filters.sortBy,
        cDtFrom: filters.cDtFrom?.format('YYYY-MM-DD') || '',
        cDtTo: filters.cDtTo?.format('YYYY-MM-DD') || '',
    });

    const fetchData = async (filters: ProductChangeFromOtherSelectedFiltersType) => {
        fetchProductChangesFromOther(getParams(filters))
            .then(data => {
                if (!!data) {
                    setData(data);
                }
            })
            .catch(err => console.error("Error occurred: ", err));
    };

    const fetchFilters = async () => {
        const response: ProductChangeFromOtherFilterType | void = await fetchProductChangesFromOtherFilters();
        if (response) {
            setFilters({ ...filters, ...response });
        }
    };

    const applyFilters = () => {
        setLoading(true);
        fetchData(selectedFilters).finally(() => setLoading(false));
    };

    const clearFilter = () => {
        setLoading(true);
        setSelectedFilters(productChangeFromOtherSelectedFiltersInitialState);
        fetchData(productChangeFromOtherSelectedFiltersInitialState).finally(() => setLoading(false));
    };

    const downloadExcel = async (filters: ProductChangeFromOtherSelectedFiltersType) => {
        const data = await downloadProductChangesFromOtherExcel(getParams(filters));
        downloadExcelData(data, "Bizim_qidaya_dəyişənlər_" + parseDate(dayjs()));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchData(selectedFilters);
        fetchFilters();
    }, []);

    return (
        <Fragment>
            <div style={{ background: '#f5f5f5', padding: 0, borderRadius: '8px', marginBottom: '24px' }}>
                <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder={t('productChange', 'productChanger')}
                            value={selectedFilters.productChanger}
                            onChange={event =>
                                setSelectedFilters({ ...selectedFilters, productChanger: event.target.value })
                            }
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder={t('productChange', 'selectInitialProduct')}
                            value={selectedFilters.initialProductIds}
                            onChange={value => setSelectedFilters({ ...selectedFilters, initialProductIds: value })}
                            options={filters?.initialProducts?.map(p => ({ label: p.name, value: p.id }))}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder={t('common', 'startDate')}
                            value={selectedFilters.cDtFrom}
                            onChange={date => setSelectedFilters({ ...selectedFilters, cDtFrom: date })}
                            format="DD/MM/YYYY"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder={t('common', 'endDate')}
                            value={selectedFilters.cDtTo}
                            onChange={date => setSelectedFilters({ ...selectedFilters, cDtTo: date })}
                            format="DD/MM/YYYY"
                        />
                    </Col>
                </Row>
                <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder={t('mediator', 'placeObject')}
                            value={selectedFilters.placeIds}
                            onChange={value => setSelectedFilters({ ...selectedFilters, placeIds: value })}
                            options={filters?.places?.map(p => ({ label: p.name, value: p.id }))}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder={t('user', 'selectUser')}
                            value={selectedFilters.prescriptionUserIds}
                            onChange={value => setSelectedFilters({ ...selectedFilters, prescriptionUserIds: value })}
                            options={filters?.users?.map(u => ({ label: u.name, value: u.id }))}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder={t('common', 'direction')}
                            value={selectedFilters.sortDir}
                            onChange={value => setSelectedFilters({ ...selectedFilters, sortDir: value })}
                            options={[
                                { label: t('common', 'ascending'), value: 'ASC' },
                                { label: t('common', 'descending'), value: 'DESC' }
                            ]}
                        />
                    </Col>
                </Row>
                <Row justify="end">
                    <Col>
                        <Space>
                            <Button
                                onClick={() => downloadExcel({ ...selectedFilters, pageSize: 1000000 })}
                            >
                                {t('prescription', 'downloadExcel')}
                            </Button>
                            <Button
                                type="primary"
                                onClick={applyFilters}
                                loading={loading}
                            >
                                {t('prescription', 'applyFilter')}
                            </Button>
                            <Button
                                danger
                                onClick={clearFilter}
                            >
                                {t('prescription', 'clearFilter')}
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            <Title level={4} style={{ textAlign: 'center', margin: '16px 0' }}>
                {t('productChange', 'toOurProduct')}
            </Title>
            <div style={{ marginBottom: '16px' }}>
                <Text>{t('common', 'totalCount')}: {data.pagination.elementCount}</Text>
            </div>

            <div style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <ProductChangeFromOtherTable
                    data={data}
                    onPageChange={(page, pageSize) => fetchData({ ...selectedFilters, page: page, pageSize })}
                />
            </div>
        </Fragment>
    );
};

export default ProductChangeFromOtherTab;
